import Vue from "vue";
import store from "@/store/store";
import { createAuth0Client } from "@auth0/auth0-spa-js";
import {ROLE_CRM_ADMINISTRATOR, ROLE_FACILITY_ADMINISTRATOR, ROLE_PORTAL_ADMINISTRATOR} from '@/acl/acl';

/** Define a default action to perform after authentication */
const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

let instance;

/** Returns the current instance of the SDK */
export const getAuthInstance = () => instance;

/** Creates an instance of the Auth0 SDK. If one has already been created, it returns that instance */
export const useAuth0 = ({
     onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
     redirectUri = window.location.origin,
     ...options
   }) =>
{
  if (instance) return instance;

  // The 'instance' is simply a Vue object
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        auth0Client: null,
        auth0ClientSetup: null,
        popupOpen: false,
        error: null,
        clearingCache: false
      };
    },
    methods: {
      /** Authenticates the user using a popup window */
      async loginWithPopup(o) {
        this.popupOpen = true;

        try {
          await this.auth0Client.loginWithPopup(o);
        } catch (e) {
          // eslint-disable-next-line
          console.error(e);
        } finally {
          this.popupOpen = false;
        }

        this.user = await this.auth0Client.getUser();
        this.isAuthenticated = true;
      },
      /** Authenticates the user using the redirect method */
      loginWithRedirect(o) {
        return this.auth0Client.loginWithRedirect(o);
      },
      /** Returns all the claims present in the ID token */
      getIdTokenClaims() {
        return this.auth0Client.getIdTokenClaims();
      },
      /** Returns the access token. If the token is invalid or missing, a new one is retrieved */
      async getTokenSilently(o, retries = 3) {
        for (let attempt = 1; attempt <= retries; attempt++) {
          try {
            // Check if auth0Client is available
            if (!this.auth0Client) {
              console.error('Auth0 client not initialized');
              return null;
            }
            
            // Check if we're on the callback page and have a user (special case for callback handling)
            const isOnCallbackPage = window.location.pathname === '/callback';
            const hasUser = this.user && this.user.sub;
            
            if (!this.isAuthenticated && !(isOnCallbackPage && hasUser)) {
              console.warn('User not authenticated, cannot get token silently - skipping API calls');
              return null;
            }
            
            const token = await this.auth0Client.getTokenSilently(o);
            
            // Validate that we got a valid token
            if (!token || typeof token !== 'string') {
              console.error('Invalid token received from Auth0');
              throw new Error('Invalid token received');
            }
            
            return token;
          } catch (e) {
            console.error(`Attempt ${attempt} failed to get token silently:`, e);
            
            // Handle specific error types
            if (e.error === 'invalid_grant' || e.error === 'consent_required' || e.statusCode === 403) {
              console.warn('Token refresh failed - clearing cache and retrying');
              
              // Clear cache and try to re-authenticate
              await this.clearAuthCache();
              
              if (attempt < retries) {
                // Try to get a new token
                try {
                  const retryToken = await this.auth0Client.getTokenSilently(o);
                  if (retryToken && typeof retryToken === 'string') {
                    return retryToken;
                  }
                } catch (retryError) {
                  console.error('Retry attempt failed:', retryError);
                }
              } else {
                // If all retries fail, redirect to login
                await this.handle403Error();
                return null;
              }
            } else if (e.error === 'login_required') {
              console.warn('Login required - redirecting to login');
              await this.handle403Error();
              return null;
            }
            
            if (attempt < retries) {
              // Wait for a short period before retrying
              await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            } else {
              // If all attempts fail, log out the user
              console.error('All attempts to get token silently failed. Logging out.');
              await store.dispatch('auth/logout');
              return null;
            }
          }
        }
      },
      /** Gets the access token using a popup window */
      getTokenWithPopup(o) {
        return this.auth0Client.getTokenWithPopup(o);
      },
      /** Logs the user out and removes their session on the authorization server */
      async logout(o) {
        try {
          console.log('Logging out user...');
          
          // Clear token health checks
          this.clearTokenHealthCheck();
          
          // Clear auth cache
          await this.clearAuthCache();
          
          return this.auth0Client.logout({
            returnTo: window.location.origin,
            ...o
          });
        } catch (e) {
          console.error('Logout error:', e);
        }
      },
      async checkAuth() {
        if (!this.isAuthenticated) {
          console.log('No user found, redirecting to login...');
          return false;
        }

        try {
          await this.fetchAndSetUser();
          return true;
        } catch (e) {
          console.error('Authentication setup failed:', e.message);
          return false;
        }
      },
      async fetchAndSetUser() {
        this.user = await this.auth0Client.getUser();

        const audience = 'https://' + process.env.VUE_APP_AUTH0_AUDIENCE;
        const metadata = this.user[audience];

        // Default roles, can set here for dev
        let roles = [ROLE_PORTAL_ADMINISTRATOR];

        if (process.env.VUE_APP_ENVIRONMENT !== "development") {
          roles = metadata ? metadata.roles.roles : [];
          roles = roles.map(({RoleName}) => RoleName);
        }

        // store user details
        await store.dispatch("auth/setUser", {
          sub: this.user.sub,
          name: this.user.name,
          email: this.user.email,
          picture: this.user.picture,
          roles,
        });

        // get access token
        await store.dispatch("auth/fetchAccessToken");
      },
      async handleCallback() {
        console.log('Handling authentication callback...');
        
        const urlParams = new URLSearchParams(window.location.search);
        await store.dispatch("auth/setAccessDenied", true);

        // handle errors
        if (urlParams.get("error")) {
          console.log('Authentication error detected');
          // password has expired
          if (urlParams.get("error_description") == "please change your password") {
            await store.dispatch("auth/setPasswordExpired", true);
          }

          // access denied
          this.loading = false;
          return;
        }

        // get user, check role and configure access token
        if (urlParams.get("code") && urlParams.get("state")) {
          // handle the redirect and retrieve tokens
          try {
            console.log('Fetching access token...');
            const {appState} = await this.auth0Client.handleRedirectCallback();
            onRedirectCallback(appState);

            // store user details
            this.user = await this.auth0Client.getUser();
            console.log('Access token retrieved successfully');

            // check user has valid role - but not on dev
            const audience = 'https://' + process.env.VUE_APP_AUTH0_AUDIENCE;
            const metadata = this.user[audience];

            // check user has valid role - but not on dev
            if (process.env.VUE_APP_ENVIRONMENT !== "development") {
              let roles = metadata.roles.roles;

              if (roles.length === 0) {
                this.loading = false;
                return;
              }

              const validRole = roles.filter((role) =>
                  role.applications[0].ExternalIdentity_Key === process.env.VUE_APP_AUTH0_CLIENT_ID
              ).length > 0;

              if (!validRole) {
                this.loading = false;
                return;
              }
            }

            // access token + user
            await this.fetchAndSetUser()

            // if we are here we must be ok
            await store.dispatch("auth/setAccessDenied", false);

            // set loading to false which will trigger redirect to dashboard
            // in Callback.vue
            this.isAuthenticated = true;
            
            // Set up token health checks
            this.setupTokenHealthCheck();
            console.log('Authentication completed successfully');

          } catch (e) {
            // likely invalid state so logout
            console.error('Error during callback handling:', e);
            await store.dispatch("auth/logout");
          }
        }
      },
      async setup() {
        console.log('Processing authentication callback...');
        
        this.auth0Client = await this.auth0ClientSetup;

        const onCallbackPage = (window.location.pathname === "/callback");
        try {
          if (onCallbackPage) {
            await this.handleCallback();
            return;
          }

          // if we are not authenticated sent to auth0
          if (!store.getters["auth/user"]) {
            console.log('No user found, redirecting to login...');
            await this.loginWithRedirect();
          }

          // we have a valid user
          console.log('User authenticated, setting up session...');
          await store.dispatch("auth/setAccessDenied", false);
          this.isAuthenticated = true;
          
          // Set up token health checks
          this.setupTokenHealthCheck();

        } catch (e) {
          console.error('Authentication setup failed:', e.message);
          this.error = e;
        }
      },
      // This method check whether we have a valid token. If we dont, we get logged out.
      // The token timer is set Tenant wide under the Settings -> Advanced page (Inactivity timeout)
      async isTokenValid() {
        try {
          if (!this.auth0Client || !this.isAuthenticated) {
            return false;
          }
          
          const isAuthenticated = await this.auth0Client.isAuthenticated();
          
          if (!isAuthenticated) {
            return false;
          }
          
          // Try to get token claims to verify token is still valid
          try {
            await this.auth0Client.getIdTokenClaims();
            return true;
          } catch (e) {
            console.warn('Token claims check failed:', e);
            return false;
          }
        } catch (e) {
          console.error('Token validation check failed:', e);
          return false;
        }
      },
      
      /** Proactively check and refresh token before it expires */
      async checkAndRefreshToken() {
        try {
          // Try to get the token silently - this will refresh if needed
          await this.getTokenSilently();
          return true;
        } catch (e) {
          console.error('Token refresh check failed:', e);
          
          // If it's a token refresh issue, handle it gracefully
          if (e.error === 'invalid_grant' || e.statusCode === 403) {
            console.warn('Proactive token refresh failed - redirecting to login');
            await this.loginWithRedirect();
          }
          return false;
        }
      },
      
      /** Clear all Auth0 related cache and tokens */
      async clearAuthCache() {
        // Prevent multiple simultaneous cache clearing operations
        if (this.clearingCache) {
          return;
        }
        
        this.clearingCache = true;
        
        try {
          // Clear localStorage cache
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.includes('auth0') || key.includes('auth') || key.includes('token'))) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => {
            localStorage.removeItem(key);
          });
          
          // Clear sessionStorage as well
          const sessionKeysToRemove = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && (key.includes('auth0') || key.includes('auth') || key.includes('token'))) {
              sessionKeysToRemove.push(key);
            }
          }
          sessionKeysToRemove.forEach(key => {
            sessionStorage.removeItem(key);
          });
          
          // Reset authentication state
          this.isAuthenticated = false;
          this.user = {};
          
        } catch (e) {
          console.error('Error clearing auth cache:', e);
        } finally {
          this.clearingCache = false;
        }
      },
      
      /** Set up periodic token health checks */
      setupTokenHealthCheck() {
        // Check token health every 5 minutes
        const checkInterval = 5 * 60 * 1000; // 5 minutes
        
        this.tokenHealthCheckInterval = setInterval(async () => {
          if (this.isAuthenticated && !this.loading) {
            try {
              await this.checkAndRefreshToken();
            } catch (e) {
              console.error('Token health check failed:', e);
            }
          }
        }, checkInterval);
      },
      
      /** Clear the token health check interval */
      clearTokenHealthCheck() {
        if (this.tokenHealthCheckInterval) {
          clearInterval(this.tokenHealthCheckInterval);
          this.tokenHealthCheckInterval = null;
        }
      },
      
      /** Handle 403 Forbidden errors specifically */
      async handle403Error() {
        console.warn('Handling 403 Forbidden error - token refresh issue detected');
        
        // Clear all cached tokens
        await this.clearAuthCache();
        
        // Show user-friendly message in console (furness.admin doesn't have setError action)
        console.warn('Your session has expired. Please log in again.');
        
        // Redirect to login with return URL
        const currentPath = window.location.pathname + window.location.search;
        await this.loginWithRedirect({
          appState: { 
            returnTo: currentPath
          }
        });
      }
    },
    /** Use this lifecycle method to instantiate the SDK client */
    async created() {
      // Create a new instance of the SDK client using members of the given options object
      this.auth0ClientSetup = createAuth0Client({
        domain: options.domain,
        clientId: options.clientId,
        authorizationParams: {
          audience: options.audience,
          redirect_uri: redirectUri,
        },
        useRefreshTokens: true,
        cacheLocation: "localstorage",
        // Add session check expiration
        sessionCheckExpiryDays: 1,
        // Add token refresh settings
        tokenRefreshMargin: 60, // Refresh token 60 seconds before expiry
      });
    }
  });

  return instance;
};

// Create a simple Vue plugin to expose the wrapper object throughout the application
export const Auth0Plugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useAuth0(options);
  }
};